import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, Wand2, Loader2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { parseNum, num } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { CameraScanner } from "../CameraScanner";
import { ProductImagePicker } from "./ProductImagePicker";

const EMPTY = { name: "", sku: "", barcode: "", category_id: "", price: 0, cost: 0, stock: 0, min_stock: 5, unit: "pcs", image_url: "", active: true };

const F = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}><Label>{label}</Label>{children}</div>
);

export const ProductForm = ({ open, onOpenChange, product, cats, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [cam, setCam] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(product ? { ...EMPTY, ...product } : EMPTY); }, [open, product]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const setN = (k) => (e) => setF((x) => ({ ...x, [k]: parseNum(e.target.value) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...f };
      delete body.id; delete body.created_at;
      if (product) await api.put(`/products/${product.id}`, body);
      else await api.post("/products", body);
      toast.success("Produk disimpan");
      onOpenChange(false);
      onSaved();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{product ? "Edit Produk" : "Tambah Produk"}</DialogTitle>
          <DialogDescription>Lengkapi informasi produk.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <F label="Nama Produk" className="sm:col-span-2"><Input required value={f.name} onChange={set("name")} data-testid="product-name-input" /></F>
          <F label="Barcode">
            <div className="flex gap-1">
              <Input value={f.barcode} onChange={set("barcode")} data-scan-target="1" placeholder="Scan / ketik" data-testid="product-barcode-input" />
              <Button type="button" size="icon" variant="outline" onClick={() => setCam(true)} title="Scan kamera" data-testid="product-barcode-cam-btn"><Camera className="h-4 w-4" /></Button>
              <Button type="button" size="icon" variant="outline" title="Generate" data-testid="product-barcode-gen-btn"
                onClick={() => setF((x) => ({ ...x, barcode: "2" + Date.now().toString().slice(-11) }))}><Wand2 className="h-4 w-4" /></Button>
            </div>
          </F>
          <F label="SKU"><Input value={f.sku} onChange={set("sku")} placeholder="Otomatis jika kosong" data-testid="product-sku-input" /></F>
          <F label="Kategori">
            <Select value={f.category_id || "none"} onValueChange={(v) => setF((x) => ({ ...x, category_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Tanpa kategori</SelectItem>
                {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Satuan"><Input value={f.unit} onChange={set("unit")} data-testid="product-unit-input" /></F>
          <F label="Harga Jual (Rp)"><Input required inputMode="numeric" className="font-mono" value={num(f.price)} onChange={setN("price")} data-testid="product-price-input" /></F>
          <F label="Harga Modal (Rp)"><Input inputMode="numeric" className="font-mono" value={num(f.cost)} onChange={setN("cost")} data-testid="product-cost-input" /></F>
          <F label="Stok"><Input inputMode="numeric" className="font-mono" value={f.stock} onChange={setN("stock")} data-testid="product-stock-input" /></F>
          <F label="Stok Minimum"><Input inputMode="numeric" className="font-mono" value={f.min_stock} onChange={setN("min_stock")} data-testid="product-minstock-input" /></F>
          <F label="Gambar Produk" className="sm:col-span-2"><ProductImagePicker value={f.image_url} onChange={(v) => setF((x) => ({ ...x, image_url: v }))} /></F>
          <div className="flex items-center gap-3 sm:col-span-2">
            <Switch checked={f.active} onCheckedChange={set("active")} data-testid="product-active-switch" />
            <span className="text-sm">Produk aktif (tampil di kasir)</span>
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button type="submit" disabled={saving} data-testid="product-save-btn">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Simpan</Button>
          </DialogFooter>
        </form>
        <CameraScanner open={cam} onOpenChange={setCam} onResult={(t) => setF((x) => ({ ...x, barcode: t }))} />
      </DialogContent>
    </Dialog>
  );
};
